/**
 * Auth Module
 */
import Vue from 'vue';
import Nprogress from 'nprogress';
import router from '../../../router/bizgain-router';
import { AuthService } from 'AppServices/authService';
import { AppService } from 'AppServices/appService';
import { LoginError } from 'GlobalErrors/loginError';
import { ForbiddenPortalRequested } from 'GlobalErrors/ForbiddenPortalRequested';
import { UserAccountExpiredException } from 'GlobalErrors/UserAccountExpiredException';
import { InvalidLoginCredentials } from 'GlobalErrors/InvalidLoginCredentials';
import { InvalidOtpException } from 'GlobalErrors/InvalidOtpException';
import { EntityDoesNotExistException } from 'GlobalErrors/EntityDoesNotExistException';
import { isJson } from 'Helpers/helpers'
import { Flavor } from 'Constants/storeTypes'
import { Languages } from '../../../constants/Languages'
import { i18n } from '../../../lang';
import { BusinessProfilesHelper } from 'Helpers/businessProfilesHelper';
import { mobilePortalDomain } from '../../../constants/PortalConfig';

const  { getBusinessProfiles } = new BusinessProfilesHelper();
import Bugsnag from '@bugsnag/js';
import { PasswordExpiredError } from '../../../errors/PasswordExpiredError';

const { login, signUpUser, loginWithEmbed, getPortalData, logout } = new AuthService();
const { getUserData, getAllLanguages, getUserById } = new AppService();
const getPortalDomain = () => {
  if (process.env.VUE_APP_CAPACITOR_ENVIRONMENT === 'true'){
    return mobilePortalDomain;
  }

  if(process.env.VUE_APP_CORDOVA_BUILD){
    return 'mobile.adbox.pro'
  }
  else if(typeof webpackHotUpdate !== 'undefined'){
    return 'roimatic.adbox.pro'
  }
  var queryParameters = new URLSearchParams(window.location.search);
  var portalDomain = queryParameters.get('portalDomain');
  // This is required for loading creative builder in DeltaX web app in embed mode.
  // In this case the portal will be the web app's portal even though the iframe is loading the roimatic portal.
  // Therefore, the iframe's url will also include the portalDomain as a query parameter
  // We'll be using this instead of the roimatic domain to get the businessProfile etc..
  if(portalDomain){
    return portalDomain;
  }
  return window.location.host;
}

const state = {
  user: isJson(localStorage.getItem('user'))
    ? JSON.parse(localStorage.getItem('user'))
    : null,
  //eslint-disable-next-line
    portalDomain: getPortalDomain(),
  // this state flag is used to indicate that a BP switch has been triggered
  isBpSwitcherOn: false,
  businessProfileId: null,
  selectedLanguageName: Languages[0].name,
  portal: null,
  currencyInfo: null,
  timezoneId: null,
  region: 'IN',
  pushNotificationToken: localStorage.getItem('pushNotificationToken') ? localStorage.getItem('pushNotificationToken') : null
};

// getters
const getters = {
  user: (state) => {
    return state.user;
  },
  portalDomain: (state) => {
    return state.portalDomain;
  },
  isBpSwitcherOn: state => state.isBpSwitcherOn,
  businessProfileId: state => state.businessProfileId,
  selectedLanguageName: state => state.selectedLanguageName,
  portal: state => state.portal,
  currencyInfo: state => state.currencyInfo,
  timezoneId: state => state.timezoneId,
  isForBizgain: state => Boolean(state.portal && state.portal.isBizgain),
  selectedBusinessProfile: (state, getters) => getters.user.businessProfiles.find(
    (bp) => bp.id === getters.businessProfileId
  ),
  region: (state) => {
    return state.region;
  },
  validRegions: state => state.portal.portalConfig.bizgainConfiguration.regionConfig,
  regionConfig: (state, getters) => getters.validRegions.find(item => item.region ===  state.region),
  portalSupportNumber: (state, getters) => {
    return getters.regionConfig.supportNumber
  },
  regionCurrencySymbol: (state, getters) => {
    return getters.regionConfig.currencySymbol
  },
  pushNotificationToken: state => state.pushNotificationToken,
  isCapacitorEnv: () => {
    if (process.env.VUE_APP_CAPACITOR_ENVIRONMENT === 'true'){
      return true;
    }

    return false
  }
};

// actions
const actions = {
  async signUpUser(context, payload) {
    const { user } = payload;
    context.commit('signUpUser');
    try {
      var response = await signUpUser(user);
      user.id = response.id;
      await login(user);
      Nprogress.done();
      setTimeout(() => {
        context.commit('signUpUserSuccess', {
          user
        });
      }, 500);
    } catch (error) {
      context.commit('signUpUserFailure');
    }
  },
  async loginUser(context, payload) {
    const { loginDetails } = payload;
    context.commit('loginUser');
    try {
      let isOTPLogin = loginDetails && loginDetails.isOTPLogin;
      let loginResponse = await login(loginDetails, isOTPLogin);
      Nprogress.done();
      let userId = loginResponse && loginResponse.userId;
      var user = await getUserById(userId);
      await context.commit('loginUserSuccess', {
        user,
        roleIds: loginResponse.roleIds
      });
      await context.commit('setUserSelectedLanguage');
    } catch (error) {
      if (error instanceof PasswordExpiredError) {
        let response = error.responseData;
        if (response.data && response.data.hash) {
          router.push({ name: 'resetPassword', query: { hash: response.data.hash, isPasswordExpired: true } });
        }
      }
      let errorDetails = {
        error: error,
        userEmail: loginDetails.emailAddress
      }
      context.commit('loginUserFailure', errorDetails);
      throw error;
    }
  },
  async loginWithEmbedToken({ commit }, embedToken) {
    try {
      let loginDetails = await loginWithEmbed(embedToken);
      Nprogress.done();
      localStorage.setItem('embedLoginDetails', JSON.stringify(loginDetails));
      if (loginDetails.isUserSignedUp) {
        localStorage.setItem('accessToken', loginDetails.accessToken);
        var user = await getUserData(loginDetails.emailAddress);
        commit('loginUserSuccess', { user });
        await commit('setUserSelectedLanguage');
        let isRoimatic = loginDetails.flavor != Flavor.deltaX;
        // fetch businessProfiles based on the flavor
        let businessProfiles = await getBusinessProfiles(isRoimatic);
        if (businessProfiles && businessProfiles.length > 0) {
          user.businessProfiles = businessProfiles;
          commit('setUser', user);
          commit('setBusinessProfileId', loginDetails.businessProfileId || businessProfiles[0].id);
        }
      }
    } catch (error) {
      let errorDetails = {
        error: error,
        userEmail: loginDetails.emailAddress
      }
      commit('loginUserFailure', errorDetails);
      throw error;
    }
  },
  async logoutUser({ commit, state }) {
    try {
      await logout();
    }
    finally {
      Nprogress.start();
    }

    commit('logoutUser');
  },
  setUser(context, payload) {
    if (payload) {
      Bugsnag.setUser(payload.id, payload.emailAddress, `${payload.firstName} ${payload.lastName}`);
    }
    context.commit('setUser', payload);
  },
  setBusinessProfileId(context, payload) {
    context.commit('setBusinessProfileId', payload);
  },
  enableBpSwitcherMode(context) {
    context.commit('setBpSwitcherMode', true);
  },
  disableBpSwitcherMode(context) {
    context.commit('setBpSwitcherMode', false);
  },
  setSelectedLanguageName(context, payload) {
    context.commit('setSelectedLanguageName', payload);
  },
  setUserSelectedLanguage(context) {
    context.commit('setUserSelectedLanguage');
  },
  setPortalDomain(context, payload) {
    context.commit('setPortalDomain', payload);
  },
  setRegion(context, payload){
    localStorage.setItem('region', payload);
    context.commit('setRegion', payload);
  },
  async setPortal(context) {
    try{
      let portalData;
      if(context.state.portalDomain === 'mobile.adbox.pro'){
        portalData = {};
      } else {
        portalData = await getPortalData(context.state.portalDomain);
      }
      if (portalData) {
        let featureFlags = portalData.featureFlags && JSON.parse(portalData.featureFlags);
        portalData.isSignupEnabled = true;
        portalData.isBizgain = false;
        portalData.isBrandPortal = false;
        portalData.isCampaignLaunchDisabled = false;
        portalData.isScheduleEditEnabled = true;
        if (featureFlags){
          if ('IsSignupEnabled' in featureFlags) {
            portalData.isSignupEnabled = featureFlags.IsSignupEnabled;
          }
          if('IsBizgain' in featureFlags) {
            portalData.isBizgain = featureFlags.IsBizgain;
          }
          if('IsBrandPortal' in featureFlags) {
            portalData.isBrandPortal = featureFlags.IsBrandPortal;
          }
          if('IsCreativeBuilderPortal' in featureFlags) {
            portalData.isCreativeBuilderPortal = featureFlags.IsCreativeBuilderPortal;
          }
          if('IsRichMediaAdBuilderPortal' in featureFlags) {
            portalData.isRichMediaAdBuilderPortal = featureFlags.IsRichMediaAdBuilderPortal;
          }
          if ('IsCampaignLaunchDisabled' in featureFlags) {
            portalData.isCampaignLaunchDisabled = featureFlags.IsCampaignLaunchDisabled;
          }
          if ('IsScheduleEditEnabled' in featureFlags) {
            portalData.isScheduleEditEnabled = featureFlags.IsScheduleEditEnabled;
          }
          if ('IsHubLifetimeBudgetCapEnabled' in featureFlags) {
            portalData.isHubLifetimeBudgetCapEnabled = featureFlags.IsHubLifetimeBudgetCapEnabled;
          }
        }
        localStorage.setItem('portal', JSON.stringify(portalData));
        context.commit('setPortal', portalData);
      }
    } catch(error){
      // if portal doesn't exist or the API call fails for some reason, then portal won't be set in state.
      // This is handled in router guard where its redirected to not found page
    }
  },
  setPushNotificationToken(context, payload){
    context.commit('setPushNotificationToken', payload);
  },
  setCurrencyInfo(context, payload) {
    context.commit('setCurrencyInfo', payload);
  },
  setTimezoneId(context, payload) {
    context.commit('setTimezoneId', payload);
  }
};

// mutations
const mutations = {
  loginUser() {
    Nprogress.start();
  },
  async setUserSelectedLanguage(state) {
    let user = state.user;
    let selectedLanguage;
    if (user && user.languageId) {
      let languages = await getAllLanguages();
      var languageCode = languages.find(l => l.id == user.languageId).format;
      languageCode = (languageCode == 'pt-PT' ? 'pt_PT' : (languageCode == 'en' ? 'en-us' : languageCode));
      selectedLanguage = Languages.find(l => l.code == languageCode);
    } else if (state.portal && state.portal.portalDefaultConfig) {
      let portalDefaultConfig = JSON.parse(state.portal.portalDefaultConfig);
      if (portalDefaultConfig && portalDefaultConfig.locale) {
        let portalLocale = portalDefaultConfig.locale;
        selectedLanguage = Languages.find(l => l.code == portalLocale);
      }
    }
    if (selectedLanguage && selectedLanguage.code) {
      i18n.locale = selectedLanguage.code
      state.selectedLanguageName = selectedLanguage.name
    } else {
      i18n.locale = Languages[0].code;
      state.selectedLanguageName = Languages[0].name
    }
  },
  async loginUserSuccess(state, loginData) {
    let { user, roleIds } = loginData;
    user.isSignedIn = true;
    user.roleIds = roleIds;
    localStorage.setItem('user', JSON.stringify(user));
    state.user = user;
    setTimeout(function () {
      Vue.notify({
        group: 'loggedIn',
        type: 'success',
        text: `Hey, ${user.firstName}!`
      });
    }, 1500);
  },
  loginUserFailure(state, errorDetails) {
    Nprogress.done();
    let error = errorDetails.error;
    if (error instanceof ForbiddenPortalRequested) {
      Vue.notify({
        group: 'transientErrors',
        type: 'error',
        text: i18n.tc('message.forbiddenPortalRequested')
      });
    } else if (error instanceof InvalidLoginCredentials) {
      Vue.notify({
        group: 'transientErrors',
        type: 'error',
        text: i18n.tc('message.invalidLoginCredentails')
      });
    } else if (error instanceof UserAccountExpiredException) {
      Vue.notify({
        group: 'transientErrors',
        type: 'error',
        text: `${i18n.tc('message.userAccountExpired')}: ${errorDetails.userEmail}`
      })
    } else if (error instanceof InvalidOtpException) {
      Vue.notify({
        group: 'transientErrors',
        type: 'error',
        text: i18n.tc('message.invalidOTP')
      })
    } else if (error instanceof EntityDoesNotExistException) {
      Vue.notify({
        group: 'transientErrors',
        type: 'info',
        text: `${i18n.tc('message.mobileNumberNotRegisterd')}`
      });
      // sending user to signup page if the number added is not registerd
      localStorage.removeItem('otpMobileNumber');
      localStorage.removeItem('emailAddress');
      localStorage.removeItem('isEmailOrMobileNumberProvided');
      router.push('/signup');
    } else if (error instanceof PasswordExpiredError) {
      // Skipping the toast notification for password expired error.
    } else {
      let loginError = new LoginError(error);
      Vue.notify({
        group: 'loggedIn',
        type: 'error',
        text: loginError.message
      });
    }
  },
  logoutUser(state) {
    state.user = null;
    state.businessProfileId = null;
    state.pushNotificationToken = null;
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('roimaticConfiguration');
    localStorage.removeItem('pushNotificationToken');
    Nprogress.done();
    router.push('/login');
    i18n.locale = 'en';
  },
  signUpUser() {
    Nprogress.start();
  },
  signUpUserSuccess(state, loginData) {
    let { user } = loginData;
    state.user = localStorage.setItem('user', user);
    Vue.notify({
      group: 'loggedIn',
      type: 'success',
      text: 'Account Created!'
    });
  },
  signUpUserFailure(state, error) {
    Nprogress.done();
    Vue.notify({
      group: 'loggedIn',
      type: 'error',
      text: error.message
    });
  },
  setUser(state, payload) {
    if (payload) {
      localStorage.setItem('user', JSON.stringify(payload));
    }
    state.user = payload;
  },
  setBusinessProfileId(state, payload) {
    state.businessProfileId = payload;
  },
  setBpSwitcherMode(state, bpSwitcherMode) {
    state.isBpSwitcherOn = bpSwitcherMode;
  },
  setSelectedLanguageName(state, payload) {
    state.selectedLanguageName = payload;
  },
  setPortal(state, payload) {
    state.portal = payload;
  },
  setPortalDomain(state, payload) {
    state.portalDomain = payload;
  },
  setCurrencyInfo(state, currencyInfo) {
    state.currencyInfo = currencyInfo;
  },
  setTimezoneId(state, timezoneId) {
    state.timezoneId = timezoneId;
  },
  setRegion(state, payload){
    state.region = payload;
  },
  setPushNotificationToken(state, payload){
    state.pushNotificationToken = payload;
    localStorage.setItem('pushNotificationToken', payload);
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
